import React, { memo, useCallback, useEffect, useState } from 'react';

import {
  Box,
  Checkbox,
  ListItemText,
  Menu,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from '@mui/material';
import debounce from 'lodash.debounce';

import { BENCHMARKS_SELECT } from '@mais-retorno-front/libs/util-benchmark-constants';
import { StyledMenuItem } from './styles';
import { IProps } from './model';
import { FiChevronUp } from '@react-icons/all-files/fi/FiChevronUp';
import { FiChevronDown } from '@react-icons/all-files/fi/FiChevronDown';

const hasSelected = (benchmark: string, list: string[]): boolean =>
  !list.find((b) => b === benchmark);

export const Benchmark = memo<IProps>(
  ({
    defaultSelectedBenchmarks,
    onSelectedBenchmarksChange,
    hasMinBenchmarkLimit = false,
    resetKey,
  }: IProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedBenchmarks, setSelectedBenchmarks] = useState(
      defaultSelectedBenchmarks || []
    );

    useEffect(() => {
      setSelectedBenchmarks(defaultSelectedBenchmarks || []);
    }, [resetKey]);

    useEffect(() => {
      if (!defaultSelectedBenchmarks) return;

      setSelectedBenchmarks(defaultSelectedBenchmarks);
    }, [defaultSelectedBenchmarks]);

    const delayed = useCallback(debounce(onSelectedBenchmarksChange, 1000), [
      onSelectedBenchmarksChange,
    ]);

    useEffect(() => {
      return delayed.cancel;
    }, [delayed]);

    const onSelectBenchmark = (benchmark: string) => {
      const exist = hasSelected(benchmark, selectedBenchmarks);

      if (exist) {
        setSelectedBenchmarks([...selectedBenchmarks, benchmark]);

        delayed([...selectedBenchmarks, benchmark]);
      } else {
        const filtered = selectedBenchmarks.filter((b) => b !== benchmark);

        if (!filtered.length && hasMinBenchmarkLimit) {
          setOpenDialog(true);
        } else {
          setSelectedBenchmarks(filtered);
          delayed(filtered);
        }
      }
    };

    const handleClick = (event: any) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    return (
      <div data-tour="profit-comparison-benchmarks-button">
        <Button
          aria-controls="benchmark-menu"
          aria-haspopup="true"
          size="small"
          variant="outlined"
          endIcon={Boolean(anchorEl) ? <FiChevronUp /> : <FiChevronDown />}
          onClick={handleClick}
          sx={(theme) => ({
            borderColor: theme.palette.primary.main,
            border: '1px solid',
            borderRadius: '4px',
            textTransform: 'none',
            fontWeight: 500,
          })}
        >
          Indicadores
        </Button>

        <Menu
          id="benchmark-menu"
          data-testid="benchmark-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          MenuListProps={{
            sx: { maxHeight: 500 },
          }}
        >
          {BENCHMARKS_SELECT.map(({ value, label }) => (
            <Box key={`benchmark-${value}`}>
              <StyledMenuItem
                value={value}
                onClick={() => onSelectBenchmark(value)}
              >
                <Checkbox checked={!hasSelected(value, selectedBenchmarks)} />
                <ListItemText primary={label} />
              </StyledMenuItem>
            </Box>
          ))}
        </Menu>

        {hasMinBenchmarkLimit && (
          <Dialog
            open={openDialog}
            onClose={() => setOpenDialog(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">Ooops...</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                É necessário ter pelo menos 1 índice selecionado.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOpenDialog(false)}
                color="primary"
                variant="contained"
                size="small"
                autoFocus
              >
                Ok
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </div>
    );
  }
);
